import { Edge, Node } from '@xyflow/react';
import { useMemo, useRef } from 'react';
import {
  AircraftLegResource,
  AviaLegTypes,
  BaseLegResourceType,
  GroundLegTypes,
  RouteLocationType,
} from '@/api/apis/route';
import { OrganType } from '@/api/apis/case';
import { RouteSegmentType } from '@/types/route';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { transformRouteLegsToSchema } from './utils';
import { getNodeTypeVariants, mainEdgeTypes } from './utils/flowConfig';
import { ReadonlyFlowCanvas } from './components/ReadonlyFlowCanvas';
import { UnconnectedLegsSchema } from './components/UnconnectedLegsSchema';

export type RouteSchemaType = {
  organType: OrganType;
  segments: RouteSegmentType[];
  locations: RouteLocationType[];
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  routeType?: 'full' | 'main';
  schemaType?: 'main' | 'alt';
  aircrafts?: BaseLegResourceType<AircraftLegResource>[];
  onClick?: () => void;
};

export const RouteSchema = ({
  organType,
  segments,
  legs,
  aircrafts,
  locations,
  routeType = 'full',
  schemaType = 'main',
  onClick,
}: RouteSchemaType) => {
  const { extendLocation } = useCountriesData();
  const nodeTypeVariants = useRef(getNodeTypeVariants(schemaType));

  const locationsExtended = useMemo(
    () => locations.map((location) => extendLocation(location)),
    [locations, extendLocation],
  );

  const { edges, nodes, blockHeight, unconnectedLegs } = useMemo(
    () =>
      transformRouteLegsToSchema({
        segments,
        legs,
        aircrafts: aircrafts || [],
        locations: locationsExtended,
        organType,
        routeType,
        schemaType,
      }),
    [aircrafts, locationsExtended, organType, legs, segments, routeType, schemaType],
  );

  const nodeBoundsWidth = useMemo(() => {
    const arr = nodes as Node[];

    return arr.reduce((max, n) => Math.max(max, n.position.x + (n.width || 0)), 0);
  }, [nodes]);

  const defaultEdgeOptions = useMemo(() => ({ label: schemaType }), [schemaType]);

  return (
    <div
      aria-label="Route Schema"
      role="button"
      tabIndex={-1}
      className={`w-full flex flex-col gap-2 pointer-events-auto ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      onKeyDown={onClick}
    >
      <ReadonlyFlowCanvas
        nodes={nodes as Node[]}
        edges={edges as Edge[]}
        nodeTypes={nodeTypeVariants.current}
        edgeTypes={mainEdgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        width={nodeBoundsWidth}
        height={blockHeight}
      />

      {unconnectedLegs.length > 0 && <UnconnectedLegsSchema legs={unconnectedLegs} schemaType={schemaType} />}
    </div>
  );
};
